'use client'

import React, { useState, useRef, useEffect, useCallback } from 'react'
import './Button.css'
import './DropdownButton.css'
import './SplitButton.css'

export type SplitButtonVariant = 'primary' | 'secondary' | 'outline' | 'danger' | 'success'
export type SplitButtonSize = 'xs' | 'sm' | 'md' | 'lg'

export interface SplitButtonOption {
  value: string
  label: string
  icon?: string
  disabled?: boolean
}

export interface SplitButtonProps {
  icon?: string
  label: string
  variant?: SplitButtonVariant
  size?: SplitButtonSize
  options: SplitButtonOption[]
  disabled?: boolean
  loading?: boolean
  className?: string
  onClick?: () => void
  onSelect?: (value: string) => void
}

interface MenuPosition {
  top?: number
  bottom?: number
  left?: number
  right?: number
}

export const SplitButton: React.FC<SplitButtonProps> = ({
  icon,
  label,
  variant = 'primary',
  size = 'md',
  options,
  disabled = false,
  loading = false,
  className = '',
  onClick,
  onSelect
}) => {
  const [isOpen, setIsOpen] = useState(false)
  const [menuPosition, setMenuPosition] = useState<MenuPosition>({})
  const containerRef = useRef<HTMLDivElement>(null)
  const menuRef = useRef<HTMLDivElement>(null)

  // Calculate menu position based on viewport
  const calculatePosition = useCallback(() => {
    if (!containerRef.current || !menuRef.current) return

    const containerRect = containerRef.current.getBoundingClientRect()
    const menuRect = menuRef.current.getBoundingClientRect()
    const viewportHeight = window.innerHeight
    const viewportWidth = window.innerWidth

    const position: MenuPosition = {}

    // Vertical: check if menu fits below
    const spaceBelow = viewportHeight - containerRect.bottom
    const spaceAbove = containerRect.top

    if (spaceBelow >= menuRect.height || spaceBelow >= spaceAbove) {
      // Open below
      position.top = containerRect.height + 4
    } else {
      // Open above
      position.bottom = containerRect.height + 4
    }

    // Horizontal: align to right edge of button
    const spaceRight = viewportWidth - containerRect.right
    const spaceLeft = containerRect.left

    if (spaceRight >= menuRect.width - containerRect.width || spaceRight >= spaceLeft) {
      // Align to left edge
      position.left = 0
    } else {
      // Align to right edge
      position.right = 0
    }

    setMenuPosition(position)
  }, [])

  // Recalculate on open
  useEffect(() => {
    if (isOpen) {
      // Small delay to ensure menu is rendered
      requestAnimationFrame(calculatePosition)
    }
  }, [isOpen, calculatePosition])

  // Close on outside click
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setIsOpen(false)
      }
    }

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside)
      return () => document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [isOpen])

  const handleSelect = (value: string) => {
    onSelect?.(value)
    setIsOpen(false)
  }

  const mainClasses = [
    'ui-btn',
    `ui-btn--${variant}`,
    `ui-btn--${size}`,
    'ui-split-btn__main',
    loading && 'ui-btn--loading',
  ].filter(Boolean).join(' ')

  const dropdownClasses = [
    'ui-btn',
    `ui-btn--${variant}`,
    `ui-btn--${size}`,
    'ui-split-btn__dropdown',
    isOpen && 'ui-split-btn__dropdown--open'
  ].filter(Boolean).join(' ')

  return (
    <div ref={containerRef} className={`ui-split-btn ${className}`}>
      <button
        type="button"
        className={mainClasses}
        onClick={onClick}
        disabled={disabled || loading}
      >
        {loading && <i className="fa-solid fa-spinner fa-spin ui-btn__spinner" />}
        {!loading && icon && <i className={`${icon} ui-btn__icon`} />}
        <span className="ui-btn__text">{label}</span>
      </button>
      
      <button
        type="button"
        className={dropdownClasses}
        onClick={() => setIsOpen(!isOpen)}
        disabled={disabled || loading}
        aria-expanded={isOpen}
        aria-haspopup="true"
      >
        <i className={`fa-duotone fa-chevron-down ui-split-btn__chevron ${isOpen ? 'ui-split-btn__chevron--open' : ''}`} />
      </button>

      {isOpen && (
        <div 
          ref={menuRef}
          className="ui-dropdown-btn__menu"
          style={{
            position: 'absolute',
            ...menuPosition
          }}
        >
          {options.map((option) => (
            <button
              key={option.value}
              type="button"
              className="ui-dropdown-btn__item"
              disabled={option.disabled}
              onClick={() => handleSelect(option.value)}
            >
              {option.icon && <i className={`${option.icon} ui-dropdown-btn__item-icon`} />}
              <span>{option.label}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  )
}

export default SplitButton
